import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { api } from "@lr-notitia/notitia-operator/client";
import type { InstanceDataspace } from "@lr-notitia/notitia-operator/schemas";
import { useGraphStore } from "./graph";
import { useDomainStore } from "./domain";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
}

export const useInstanceDataspacesStore = defineStore("InstanceDataspaces", () => {
	let domainId = "";
	const dataspaces = ref<InstanceDataspace[]>([]);

	function loadDataspaces(dId: string): Promise<void> {
		domainId = dId;
		return new Promise((resolve) => {
			api()
				.domain(domainId)
				.dataspaces()
				.get()
				.then((data) => {
					dataspaces.value = data;
					resolve();
				})
				.catch(() => console.warn("Failed to load dataspaces"));

			api()
				.socket()
				.domain(domainId)
				.instance()
				.dataspaces()
				.on((message) => {
					switch (message.method) {
						case "create":
							if (!dataspaces.value.find((d) => d.id === message.data.id))
								dataspaces.value.push(message.data);
							break;
						case "update":
							dataspaces.value = dataspaces.value.map((dataspace) => {
								if (dataspace.id === message.data.id) return message.data;
								return dataspace;
							});
							break;
						case "delete":
							dataspaces.value = dataspaces.value.filter((dataspace) => dataspace.id !== message.data);
							break;
					}
				});
		});
	}
	function clearDataspaces() {
		api().socket().domain(domainId).instance().dataspaces().off();
		dataspaces.value = [];
		domainId = "";
	}
	function getById(id: string): InstanceDataspace | undefined {
		return dataspaces.value.find((dataspace) => dataspace.id === id);
	}
	function addDataspace(
		templateId: string,
		version: string,
		name: string,
		description: string = "",
		overrideAccessCheck: boolean = false
	): Promise<{ id: string; dataspace: InstanceDataspace }> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!overrideAccessCheck) {
				if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			}
			api()
				.domain(domainId)
				.dataspaces()
				.post(templateId, version, name, description)
				.then((data) => {
					resolve(data);
				})
				.catch((e) => reject(e));
		});
	}
	function updateDataspace(dataspace: InstanceDataspace): Promise<InstanceDataspace> {
		if (!graphStore || domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.dataspace(dataspace.id)
				.put({ dataspace })
				.then((data) => {
					dataspaces.value = dataspaces.value.map((d) => {
						if (d.id === data.id) return data;
						return d;
					});
					resolve(data as InstanceDataspace);
				})
				.catch(() => reject());
		});
	}
	function removeDataspace(id: string): Promise<void> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			let dataspace = getById(id);
			if (!dataspace) return reject();
			api()
				.domain(domainId)
				.dataspace(id)
				.del()
				.then((data) => {
					dataspaces.value = dataspaces.value.filter((d) => d.id !== id);
					resolve();
				})
				.catch(() => reject());
		});
	}
	function mongoDbgetRegisteredDataspaces(domainId: string): Promise<string[]> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.dataspaces()
				.getRegisteredDataspaces()
				.then((data) => {
					resolve(data);
				})
				.catch(() => reject());
		});
	}
	function mongoDbRegisterDataspace(domainId: string, dataspaceId: string): Promise<void> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.dataspace(dataspaceId)
				.registerMongoDB()
				.then(() => resolve())
				.catch(() => reject());
		});
	}
	function mongoDbUnregisterDataspace(domainId: string, dataspaceId: string): Promise<void> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.dataspace(dataspaceId)
				.unregisterMongoDB()
				.then(() => resolve())
				.catch(() => reject());
		});
	}

	const openedDataspaceId = ref<string | undefined>(undefined);
	const openedDataspace = computed(() => {
		if (!openedDataspaceId.value) return undefined;
		return getById(openedDataspaceId.value);
	});

	const environmentVariables = ref<Record<string, string>>({});
	async function loadEnvironmentVariables(domainId: string, dataspaceId: string) {
		environmentVariables.value = await api()
			.domain(domainId)
			.dataspace(dataspaceId)
			.environment()
			.getEnvironmentVariables()
			.catch(() => ({}));
	}

	async function getPermittedUserIds(domainId: string, dataspaceId: string): Promise<string[]> {
		return await api()
			.domain(domainId)
			.dataspace(dataspaceId)
			.permissions()
			.get()
			.catch(() => []);
	}
	async function addPermittedUserId(domainId: string, dataspaceId: string, userId: string): Promise<boolean> {
		return await api()
			.domain(domainId)
			.dataspace(dataspaceId)
			.permissions()
			.add(userId)
			.then(() => true)
			.catch(() => false);
	}
	async function removePermittedUserId(domainId: string, dataspaceId: string, userId: string): Promise<boolean> {
		return await api()
			.domain(domainId)
			.dataspace(dataspaceId)
			.permissions()
			.remove(userId)
			.then(() => true)
			.catch(() => false);
	}

	async function getAllowAll(domainId: string, dataspaceId: string): Promise<boolean> {
		return await api()
			.domain(domainId)
			.dataspace(dataspaceId)
			.permissions()
			.getAllowAll()
			.catch(() => false);
	}

	async function setAllowAll(domainId: string, dataspaceId: string, allowAll: boolean): Promise<boolean> {
		return await api()
			.domain(domainId)
			.dataspace(dataspaceId)
			.permissions()
			.setAllowAll(allowAll)
			.then(() => true)
			.catch(() => false);
	}

	watch(
		() => dataspaces.value,
		() => {
			if (!graphStore) init();
			dataspaces.value.sort((a, b) => {
				if (a.label < b.label) return -1;
				if (a.label > b.label) return 1;
				return 0;
			});
		}
	);
	watch(
		() => openedDataspaceId.value,
		(newId, oldId) => {
			if (oldId) {
				api().socket().domain(domainId).instance().dataspace(oldId).off();
			}
			if (newId) {
				api()
					.socket()
					.domain(domainId)
					.instance()
					.dataspace(newId)
					.on((message) => {
						const d = dataspaces.value?.find((dataspace) => dataspace.id === message.id);
						if (!d) return;
						d.label = message.label;
						d.workspaces = message.workspaces;

						if (openedDataspace.value) {
							openedDataspace.value.label = message.label;
							openedDataspace.value.workspaces = message.workspaces;
						}
					});
			}
		}
	);

	return {
		dataspaces,
		loadDataspaces,
		clearDataspaces,
		getById,
		addDataspace,
		updateDataspace,
		removeDataspace,
		mongoDbgetRegisteredDataspaces,
		mongoDbRegisterDataspace,
		mongoDbUnregisterDataspace,
		openedDataspaceId,
		openedDataspace,
		environmentVariables,
		loadEnvironmentVariables,

		getPermittedUserIds,
		addPermittedUserId,
		removePermittedUserId,
		getAllowAll,
		setAllowAll,
	};
});
